import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Checkbox, Form, Input, Modal, Radio, Select, Space, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import {
  EditOutlined,
  InfoCircleOutlined,
  LockOutlined,
  MailOutlined,
  PlusOutlined,
  SearchOutlined,
  StopOutlined,
  SyncOutlined,
  UnlockOutlined,
} from '@ant-design/icons'
import { checkNewEmailStatus } from '../api/admin'
import { useToast } from '../contexts/ToastContext'
import {
  useAdminUserDetail,
  useAdminUsers,
  useCheckUserExistsMutation,
  useBulkInviteUsersMutation,
  useCreateAdminUserMutation,
  useDeleteAdminUserMutation,
  useRecreateAdminUserMutation,
  useReinviteUserMutation,
  useRevokeUserMutation,
  useUnlockUserMutation,
  useUpdateAdminUserMutation,
} from '../hooks/useAdminUsers'
import { formatPhone, normalizePhone } from '../lib/phone'
import { personNameSchema, requiredEmailSchema, zodAntdValidator } from '../lib/validation'
import type { AdminUserFormValues, AdminUserRow, AdminUserStatus } from '../types/admin'
import { updateSession, useAuthSession } from '../lib/session'

type ModalState = {
  open: boolean
  mode: 'create' | 'edit'
  userId: string
}

type FormValues = {
  firstName: string
  lastName: string
  phone: string
  email: string
  typeId: number
  statusId: number
}

type EmailStatus = 'free' | 'exists' | 'pending' | 'deleted'

const userTypeOptions = [{ label: 'Super Admin', value: 1 }]
const statusOptions = [
  { label: 'Active', value: 1 },
  { label: 'Inactive', value: 2 },
]

const firstNameSchema = personNameSchema('First Name')
const lastNameSchema = personNameSchema('Last Name')
const emailSchema = requiredEmailSchema('Email')

function statusTag(status: AdminUserStatus) {
  if (status === 'Active') return <Tag color="green">Active</Tag>
  if (status === 'Inactive') return <Tag color="default">Inactive</Tag>
  if (status === 'Invited') return <Tag color="cyan">Invited</Tag>
  if (status === 'Expired') return <Tag color="volcano">Expired</Tag>
  return <Tag color="gold">For Invite</Tag>
}

export default function Admin() {
  const authSession = useAuthSession()
  const { addToast } = useToast()

  const [fullNameFilter, setFullNameFilter] = useState('')
  const [hideInactive, setHideInactive] = useState(false)
  const defaultPageSize =
    authSession?.defaultPageSize && authSession.defaultPageSize > 0 ? authSession.defaultPageSize : 25
  const [pagination, setPagination] = useState({ current: 1, pageSize: defaultPageSize })
  const [modalState, setModalState] = useState<ModalState>({ open: false, mode: 'create', userId: '' })
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [bulkInviteSent, setBulkInviteSent] = useState(false)
  const [bulkSelectedUserIds, setBulkSelectedUserIds] = useState<string[]>([])
  const [initialEmail, setInitialEmail] = useState('')
  const [initialFormValues, setInitialFormValues] = useState<FormValues | null>(null)
  const [hasEmailUpdate, setHasEmailUpdate] = useState<boolean | null>(null)
  const [isLocked, setIsLocked] = useState(false)
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('free')
  const [existingEmailText, setExistingEmailText] = useState('')

  const [form] = Form.useForm<FormValues>()
  const isEmailLockedForPendingVerification = modalState.mode === 'edit' && hasEmailUpdate === true

  const { data: users = [], isLoading, isFetching, isError, error, refetch } = useAdminUsers({
    fullName: fullNameFilter || undefined,
    hideInactive,
  })
  const { data: allUsers = [] } = useAdminUsers()
  const { data: editDetail, isFetching: isDetailFetching } = useAdminUserDetail(
    modalState.mode === 'edit' ? modalState.userId : ''
  )
  const canEditStatus = modalState.mode === 'edit' && (editDetail?.statusId === 1 || editDetail?.statusId === 2)
  const isEditDetailLoading = modalState.mode === 'edit' && (!editDetail || isDetailFetching)

  const createMutation = useCreateAdminUserMutation()
  const recreateMutation = useRecreateAdminUserMutation()
  const updateMutation = useUpdateAdminUserMutation()
  const deleteMutation = useDeleteAdminUserMutation()
  const bulkInviteMutation = useBulkInviteUsersMutation()
  const reinviteMutation = useReinviteUserMutation()
  const revokeMutation = useRevokeUserMutation()
  const unlockMutation = useUnlockUserMutation()
  const checkExistsMutation = useCheckUserExistsMutation()

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }))
  }, [fullNameFilter, hideInactive])

  useEffect(() => {
    if (modalState.mode !== 'edit' || !editDetail) return

    form.setFieldsValue({
      firstName: editDetail.firstName,
      lastName: editDetail.lastName,
      phone: editDetail.phone,
      email: editDetail.email,
      typeId: editDetail.typeId || 1,
      statusId: editDetail.statusId || 1,
    })
    setInitialEmail(editDetail.email)
    setInitialFormValues({
      firstName: editDetail.firstName,
      lastName: editDetail.lastName,
      phone: editDetail.phone,
      email: editDetail.email,
      typeId: editDetail.typeId || 1,
      statusId: editDetail.statusId || 1,
    })
    setIsLocked(Boolean(editDetail.isLocked))
  }, [editDetail, form, modalState.mode])

  const openCreate = () => {
    form.setFieldsValue({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      typeId: 1,
      statusId: 1,
    })
    setInitialEmail('')
    setInitialFormValues({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      typeId: 1,
      statusId: 1,
    })
    setHasEmailUpdate(null)
    setIsLocked(false)
    setEmailStatus('free')
    setExistingEmailText('')
    setModalState({ open: true, mode: 'create', userId: '' })
  }

  const openEdit = useCallback((row: AdminUserRow) => {
    form.resetFields()
    setModalState({ open: true, mode: 'edit', userId: row.id })
    setInitialFormValues(null)
    setInitialEmail('')
    setHasEmailUpdate(null)
    setIsLocked(false)
    setEmailStatus('free')
    setExistingEmailText('')
  }, [form])

  useEffect(() => {
    if (!modalState.open || modalState.mode !== 'edit' || !initialEmail) return

    let cancelled = false
    setHasEmailUpdate(null)

    void checkNewEmailStatus(initialEmail)
      .then((status) => {
        if (!cancelled) {
          setHasEmailUpdate(Boolean(status?.hasEmailUpdate))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasEmailUpdate(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [initialEmail, modalState.mode, modalState.open])

  const closeModal = () => {
    setModalState({ open: false, mode: 'create', userId: '' })
    form.resetFields()
    setInitialEmail('')
    setInitialFormValues(null)
    setHasEmailUpdate(null)
    setIsLocked(false)
    setEmailStatus('free')
    setExistingEmailText('')
  }

  const handleStatusAction = useCallback(async (user: AdminUserRow, action: 'reinvite' | 'revoke') => {
    try {
      if (action === 'reinvite') await reinviteMutation.mutateAsync(user.id)
      if (action === 'revoke') await revokeMutation.mutateAsync(user.id)
      addToast(`${action === 'reinvite' ? 'Reinvite' : 'Revoke'} sent`, 'success')
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Action failed', 'error')
    }
  }, [addToast, reinviteMutation, revokeMutation])

  const unlock = useCallback(async (row: AdminUserRow) => {
    try {
      const response = await unlockMutation.mutateAsync(row.id)
      if (response?.code === 'ResetPasswordAlreadySent') {
        addToast('Password reset email already sent to this user', 'warning')
      } else {
        addToast('Password reset email sent', 'success')
      }
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Unlock failed', 'error')
    }
  }, [addToast, unlockMutation])

  const showStatusSelection = canEditStatus

  const processUpdate = async (payload: AdminUserFormValues, email: string, values: FormValues) => {
    const detail = await updateMutation.mutateAsync(payload)
    const changedEmail = email !== initialEmail
    const normalizedCurrent = {
      firstName: payload.firstName,
      lastName: payload.lastName,
      phone: payload.phone,
      typeId: payload.typeId,
      statusId: payload.statusId,
    }
    const normalizedInitial = initialFormValues
      ? {
          firstName: initialFormValues.firstName.trim(),
          lastName: initialFormValues.lastName.trim(),
          phone: normalizePhone(initialFormValues.phone),
          typeId: initialFormValues.typeId,
          statusId: initialFormValues.statusId,
        }
      : null
    const hasOtherChanges = normalizedInitial ? JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedInitial) : true

    if (detail?.success && changedEmail && values.statusId === 1 && !isLocked) {
      if (detail?.hasChangedEmail) {
        addToast(`A verification email has been sent to ${email}.`, 'info')
      } else {
        addToast(`A verification email has already been sent to ${email}.`, 'info')
      }
    } else if (detail?.success && changedEmail && values.statusId === 2) {
      addToast(
        hasOtherChanges
          ? 'Changes have been saved. Email update not applied - user must be in Active status to update email'
          : 'Email update not applied - user must be in Active status to update email',
        'warning'
      )
    } else if (detail?.success && changedEmail && isLocked) {
      addToast(
        hasOtherChanges
          ? 'Changes have been saved. Email update not applied - user must be Unlocked to update email'
          : 'Email update not applied - user must be Unlocked to update email',
        'warning'
      )
    } else if (detail?.success) {
      addToast('User updated', 'success')
    } else {
      addToast('Unable to update user', 'error')
      return
    }

    closeModal()
  }

  const saveUser = async () => {
    const saveUserInternal = async (values: FormValues, skipEmailChangeWarning = false) => {
      const normalizedPhone = normalizePhone(values.phone)
      if (normalizedPhone.length !== 10) {
        addToast('Phone number must be exactly 10 digits.', 'error')
        return
      }

      const email = values.email.trim()
      const shouldConfirmEmailChange =
        modalState.mode === 'edit' &&
        email !== initialEmail &&
        values.statusId === 1 &&
        hasEmailUpdate === false &&
        !isLocked

      if (shouldConfirmEmailChange && !skipEmailChangeWarning) {
        Modal.confirm({
          title: 'Update Email',
          content: `If you proceed with changing this email from ${initialEmail} to ${email}, you will not be able to make any email related changes for the next 24 hours. Do you want to continue?`,
          onOk: async () => saveUserInternal(values, true),
        })
        return
      }

      let pendingUserId: string | undefined
      let canRecreate = false
      let userOverride = false

      if (modalState.mode === 'create' || email !== initialEmail) {
        const existsState = await checkExistsMutation.mutateAsync(email)
        userOverride = Boolean(existsState?.userOverride)
        canRecreate = Boolean(existsState?.deleted || userOverride)
        pendingUserId = typeof existsState?.pending === 'string' ? existsState.pending : undefined
        if (existsState?.deleted) {
          setEmailStatus('deleted')
        } else if (pendingUserId) {
          setEmailStatus('pending')
        } else if (existsState?.exists && !existsState?.userOverride) {
          setEmailStatus('exists')
          setExistingEmailText(email)
        } else {
          setEmailStatus('free')
          setExistingEmailText('')
        }

        if (existsState?.exists && !existsState?.userOverride) {
          addToast('User already exists. Please use another email.', 'error')
          return
        }
      }

      const payload: AdminUserFormValues = {
        userId: modalState.mode === 'edit' ? modalState.userId : undefined,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        phone: normalizedPhone,
        email,
        typeId: values.typeId,
        statusId: modalState.mode === 'edit'
          ? (canEditStatus ? values.statusId : (editDetail?.statusId ?? values.statusId))
          : 1,
        defaultClientId: modalState.mode === 'edit' ? (editDetail?.defaultClientId || undefined) : undefined,
        isrlc: false,
        userOverride,
      }

      if (modalState.mode === 'create') {
        const createPayload = {
          ...payload,
          userId: pendingUserId || payload.userId,
        }
        if (canRecreate) {
          await recreateMutation.mutateAsync(createPayload)
        } else {
          await createMutation.mutateAsync(createPayload)
        }
        addToast('DI admin user created', 'success')
        closeModal()
        return
      }

      await processUpdate(payload, email, values)
    }

    try {
      const values = await form.validateFields()
      await saveUserInternal(values)
    } catch (e) {
      if (typeof e === 'object' && e !== null && 'errorFields' in e) return
      addToast(e instanceof Error ? e.message : 'Unable to save user', 'error')
    }
  }

  const deleteUser = async () => {
    if (modalState.mode !== 'edit' || !modalState.userId) return
    Modal.confirm({
      title: 'Delete user',
      content: 'Are you sure you want to delete this user?',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        await deleteMutation.mutateAsync(modalState.userId)
        addToast('User deleted', 'warning')
        closeModal()
      },
    })
  }

  const columns: ColumnsType<AdminUserRow> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'fullName',
        key: 'fullName',
        sorter: (a, b) => a.fullName.localeCompare(b.fullName),
        render: (_, row) => (
          <button
            type="button"
            onClick={() => void openEdit(row)}
            className="font-medium text-gray-900 hover:text-brand-600 text-left"
          >
            {row.fullName}
          </button>
        ),
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        key: 'phone',
        render: (v: string) => formatPhone(v),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'User Type',
        dataIndex: 'role',
        key: 'role',
      },
      {
        title: 'Status',
        key: 'status',
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            {statusTag(row.status)}
          </Space>
        ),
      },
      {
        title: 'Locked',
        key: 'isLocked',
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            {row.isLocked ? <Tag color="volcano">Locked</Tag> : <Tag color="green">Unlocked</Tag>}
          </Space>
        ),
      },
      {
        title: 'Last Login',
        dataIndex: 'lastLogin',
        key: 'lastLogin',
        render: (value: string | null) =>
          value
            ? new Date(value).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })
            : '—',
      },
      {
        title: 'Actions',
        key: 'action',
        width: 170,
        render: (_, row) => (
          <Space size={2}>
            {(row.statusId === 6 || row.statusId === 7) && (
              <Tooltip title={row.statusId === 7 ? 'Invite' : 'Reinvite'}>
                <Button
                  type="text"
                  icon={<MailOutlined />}
                  aria-label={row.statusId === 7 ? 'Invite user' : 'Reinvite user'}
                  onClick={() => void handleStatusAction(row, 'reinvite')}
                />
              </Tooltip>
            )}
            {row.statusId === 5 && (
              <Tooltip title="Revoke">
                <Button
                  type="text"
                  icon={<StopOutlined />}
                  aria-label="Revoke invite"
                  onClick={() => void handleStatusAction(row, 'revoke')}
                />
              </Tooltip>
            )}
            {row.isLocked && (
              <Tooltip title="Unlock">
                <Button
                  type="text"
                  icon={<UnlockOutlined />}
                  aria-label="Unlock user"
                  onClick={() => void unlock(row)}
                />
              </Tooltip>
            )}
            <Tooltip title="Edit user">
              <Button
                type="text"
                icon={<EditOutlined />}
                aria-label="Edit user"
                onClick={() => void openEdit(row)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [handleStatusAction, openEdit, unlock]
  )

  const onTableChange = (nextPagination: TablePaginationConfig) => {
    const nextPageSize = nextPagination.pageSize ?? pagination.pageSize
    setPagination((prev) => ({
      ...prev,
      current: nextPagination.current ?? prev.current,
      pageSize: nextPageSize,
    }))
    if (nextPageSize !== authSession?.defaultPageSize) {
      updateSession({ defaultPageSize: nextPageSize })
    }
  }

  const bulkOptions = useMemo(
    () =>
      allUsers.map((user) => ({
        label: user.fullName,
        value: user.id,
      })),
    [allUsers]
  )

  const openBulkEmailModal = () => {
    setBulkSelectedUserIds([])
    setBulkInviteSent(false)
    setBulkModalOpen(true)
  }

  const closeBulkEmailModal = () => {
    setBulkModalOpen(false)
    setBulkSelectedUserIds([])
    setBulkInviteSent(false)
  }

  const sendBulkInvite = async () => {
    try {
      await bulkInviteMutation.mutateAsync(bulkSelectedUserIds)
      setBulkInviteSent(true)
      addToast('Emails have been sent to selected users', 'success')
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Failed to send bulk invite', 'error')
    }
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white via-white to-sky-50/40">
      <div
        className="pointer-events-none absolute -top-16 -right-16 h-44 w-44 rounded-full bg-sky-100/70 blur-2xl"
        aria-hidden
      />
      <div className="px-6 pt-5 pb-4 border-b border-brand-100">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-gray-900 tracking-tight">DI Users</h1>
            <p className="text-sm text-gray-500 mt-1">Administration workspace for Dashboard Insights users</p>
          </div>
          <Tag color="cyan-inverse" className="px-3 py-1 rounded-full font-medium">
            {users.length} users
          </Tag>
        </div>
        <div className="mt-3 h-1.5 w-16 rounded-full bg-gradient-to-r from-sky-400 to-brand-500" aria-hidden />
      </div>

      <div className="p-4 bg-white/70 backdrop-blur-[1px]">
        <Space wrap size={12}>
          <Button icon={<PlusOutlined />} onClick={openCreate} aria-label="Add new DI user">
            Add
          </Button>
          <Tooltip title="Send invite emails to multiple users">
            <Button icon={<MailOutlined />} aria-label="Bulk email" onClick={openBulkEmailModal}>
              Bulk Email
            </Button>
          </Tooltip>
          <Input
            placeholder="Name"
            value={fullNameFilter}
            onChange={(e) => setFullNameFilter(e.target.value)}
            suffix={<SearchOutlined className="text-brand-500" />}
            style={{ width: 220 }}
            allowClear
          />
          <Checkbox checked={hideInactive} onChange={(e) => setHideInactive(e.target.checked)}>
            Hide Inactive Users
          </Checkbox>
          <Tooltip title="Refresh table">
            <Button
              icon={<SyncOutlined />}
              aria-label="Refresh users table"
              onClick={() => {
                refetch()
                addToast('DI users refreshed', 'success')
              }}
            />
          </Tooltip>
          {(isLoading || isFetching) && (
            <span className="text-sm text-gray-500 inline-flex items-center gap-1.5">
              <span className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
              Loading...
            </span>
          )}
        </Space>
      </div>

      <div className="p-4">
        {isError && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={(error as Error)?.message || 'Failed to load DI users'}
          />
        )}
        <Table<AdminUserRow>
          rowKey="id"
          columns={columns}
          dataSource={users}
          loading={isLoading || isFetching}
          onChange={onTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: users.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0] ?? 0} - ${range[1] ?? 0} of ${total}`,
          }}
        />
      </div>

      <Modal
        open={modalState.open}
        title={modalState.mode === 'create' ? 'Add New DI Admin User' : 'Edit DI Admin User'}
        onCancel={closeModal}
        onOk={() => void saveUser()}
        okText={modalState.mode === 'create' ? 'Create' : 'Update'}
        okButtonProps={{
          loading:
            createMutation.isPending ||
            recreateMutation.isPending ||
            updateMutation.isPending ||
            checkExistsMutation.isPending,
          disabled: isEditDetailLoading,
        }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <Space>
            {modalState.mode === 'edit' && (
              <Button
                danger
                icon={<StopOutlined />}
                loading={deleteMutation.isPending}
                onClick={() => void deleteUser()}
                disabled={authSession?.currentUserId === modalState.userId}
              >
                Delete
              </Button>
            )}
            <CancelBtn />
            <OkBtn />
          </Space>
        )}
      >
        <Form form={form} layout="vertical" disabled={isEditDetailLoading}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[
                { validator: zodAntdValidator(firstNameSchema) },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[
                { validator: zodAntdValidator(lastNameSchema) },
              ]}
            >
              <Input />
            </Form.Item>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Form.Item
              name="phone"
              label="Phone"
              rules={[
                { required: true, message: 'Phone is required.' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve()
                    return normalizePhone(value).length === 10
                      ? Promise.resolve()
                      : Promise.reject(new Error('Phone number must be exactly 10 digits.'))
                  },
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="email"
              label={(
                <span className="inline-flex items-center gap-1">
                  <span>Email</span>
                  {isEmailLockedForPendingVerification && (
                    <Tooltip title="A verification email has already been sent. Email can be updated again after 24 hours.">
                      <InfoCircleOutlined className="text-red-500" />
                    </Tooltip>
                  )}
                </span>
              )}
              rules={[
                { validator: zodAntdValidator(emailSchema) },
              ]}
            >
              <Input disabled={isEmailLockedForPendingVerification} />
            </Form.Item>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Form.Item name="typeId" label="Role" rules={[{ required: true, message: 'Role is required.' }]}>
              <Radio.Group options={userTypeOptions} />
            </Form.Item>
            {showStatusSelection && (
              <Form.Item name="statusId" label="Status" rules={[{ required: true, message: 'Status is required.' }]}>
                <Radio.Group options={statusOptions} />
              </Form.Item>
            )}
          </div>

          {emailStatus === 'pending' && <Alert type="info" showIcon message="User is pending creation." />}
          {emailStatus === 'deleted' && (
            <Alert
              type="info"
              showIcon
              message="A deleted user exists for this email and will be restored when saved."
            />
          )}
          {emailStatus === 'exists' && (
            <Alert type="error" showIcon message={`User already exists for email: ${existingEmailText}`} />
          )}
          {isEditDetailLoading && (
            <Alert type="info" showIcon message="Loading user details..." />
          )}
          {isLocked && (
            <Alert
              type="warning"
              showIcon
              icon={<LockOutlined />}
              message="User is currently locked. Email updates are restricted until unlocked."
            />
          )}
        </Form>
      </Modal>

      <Modal
        open={bulkModalOpen}
        title="Bulk Invite"
        onCancel={closeBulkEmailModal}
        onOk={() => void (bulkInviteSent ? closeBulkEmailModal() : sendBulkInvite())}
        okText={bulkInviteSent ? 'OK' : 'Send Email'}
        okButtonProps={{
          loading: bulkInviteMutation.isPending,
          disabled: !bulkInviteSent && bulkSelectedUserIds.length === 0,
        }}
        cancelButtonProps={{ style: { display: bulkInviteSent ? 'none' : undefined } }}
      >
        {!bulkInviteSent ? (
          <Form layout="vertical">
            <Form.Item label="Users">
              <Select
                mode="multiple"
                allowClear
                placeholder="Select users"
                options={bulkOptions}
                value={bulkSelectedUserIds}
                onChange={(value) => setBulkSelectedUserIds(value)}
                optionFilterProp="label"
              />
            </Form.Item>
          </Form>
        ) : (
          <Alert type="success" showIcon message="Emails have been sent to the selected users." />
        )}
      </Modal>
    </div>
  )
}
